import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { catchError } from 'rxjs/operators';
import { of, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class HubSpokeAcknowledgeService {
  private readonly storageKey = 'hubSpokeAcknowledge';
  private readonly tempStorageKey = 'hubSpokeTempAck';

  constructor(private http: HttpService) {}

  /**
   * Helper to format a date to "YYYY-MM-DD HH:mm:ss" in local time.
   */
  private formatDate(date: Date): string {
    const pad = (num: number) => num.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  /**
   * Parse flight leg details and passenger counts and store a temporary acknowledgment in localStorage.
   */
  storeTempAcknowledgement(flight: any, passengers: any): void {
    try {
      if (!flight || !flight.flightlegs) {
        return;
      }
      const bounds = Array.isArray(flight.flightlegs[0]) ? flight.flightlegs : [flight.flightlegs];
      const onwardLegs = bounds[0];
      const returnLegs = bounds[1];

      const tempAck = {
        carriers: onwardLegs.map((leg: any) => leg.carrier),
        flight_numbers: onwardLegs.map((leg: any) => leg.flightnumber),
        sectors: onwardLegs.map((leg: any) => `${leg.origin}-${leg.destination}`),
        onward_date: onwardLegs[0]?.depdate || '',
        return_date: (returnLegs && returnLegs[0]?.depdate) || '',
        multi_city_dates: {},
        travelers: [
          passengers.adults || 0,
          passengers.children || 0,
          passengers.infants || 0
        ],
        acknowledged_at: this.formatDate(new Date())
      };

      localStorage.setItem(this.tempStorageKey, JSON.stringify(tempAck));
      localStorage.setItem(this.storageKey, JSON.stringify(tempAck));
    } catch (e) {
      // Fail-silent fallback
    }
  }

  /**
   * Submit the final acknowledgment payload with the booking ID to the backend.
   * Then clear the keys from localStorage.
   */
  submitAcknowledgement(bookingId: number): Observable<any> {
    let ackData: any = null;

    try {
      const tempStr = localStorage.getItem(this.tempStorageKey);
      const permStr = localStorage.getItem(this.storageKey);
      
      if (tempStr) {
        ackData = JSON.parse(tempStr);
      } else if (permStr) {
        ackData = JSON.parse(permStr);
      }
    } catch (e) {
      // Fail-silent fallback
    }

    if (!ackData) {
      return of({ success: false, message: 'No acknowledgment found to submit' });
    }

    const payload = {
      booking_id: bookingId,
      source: 1,
      acknowledgement_status: true,
      acknowledged_at: ackData.acknowledged_at,
      carriers: ackData.carriers,
      flight_numbers: ackData.flight_numbers,
      sectors: ackData.sectors,
      data: {
        onward_date: ackData.onward_date,
        return_date: ackData.return_date,
        multi_city_dates: ackData.multi_city_dates || {},
        travelers: ackData.travelers
      }
    };

    return this.http.flightsave('apiV2/booking-class/air-cart-acknowledgements', payload).pipe(
      catchError(err => {
        return of({ success: false, error: err });
      })
    );
  }

  /**
   * Get the stored acknowledgment object from localStorage.
   */
  getAcknowledgement(): any {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Remove both temporary and permanent acknowledgment keys from localStorage.
   */
  clearAcknowledgement(): void {
    try {
      localStorage.removeItem(this.storageKey);
      localStorage.removeItem(this.tempStorageKey);
    } catch (e) {
      // Fail-silent fallback
    }
  }

  /**
   * Submit the acknowledgment directly to the backend without using localStorage.
   */
  submitDirectAcknowledgement(bookingId: number, flight: any, passengers: any, status: boolean = true): Observable<any> {
    const bounds = Array.isArray(flight.flightlegs[0]) ? flight.flightlegs : [flight.flightlegs];
    const onwardLegs = bounds[0];
    const returnLegs = bounds[1];

    const payload = {
      booking_id: bookingId,
      source: 1,
      acknowledgement_status: status,
      acknowledged_at: this.formatDate(new Date()),
      carriers: onwardLegs.map((leg: any) => leg.carrier),
      flight_numbers: onwardLegs.map((leg: any) => leg.flightnumber.toString()),
      sectors: onwardLegs.map((leg: any) => `${leg.origin}-${leg.destination}`),
      data: {
        onward_date: onwardLegs[0]?.depdate || '',
        return_date: (returnLegs && returnLegs[0]?.depdate) || '',
        multi_city_dates: {},
        travelers: [
          passengers.adults || 0,
          passengers.children || 0,
          passengers.infants || 0
        ]
      }
    };

    return this.http.flightsave('apiV2/booking-class/air-cart-acknowledgements', payload).pipe(
      catchError(err => {
        return of({ success: false, error: err });
      })
    );
  }

  getAckStatus(bookingId: string | number): Observable<any> {
    return this.http.flightgetall(`apiV2/booking-class/air-cart-ack-status?booking_id=${bookingId}`).pipe(
      catchError(err => {
        return of({ success: false, error: err });
      })
    );
  }
}
